import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { LiveReportList } from '@futrli-sdk/predict';

import { ActionDialogWizard, useDialogWizardState } from '@futrli/ui-components/components/dialog-wizard';
import { useApiView } from '@futrli/views/hooks/useApiView';
import { ModifyReportView } from '@futrli/views/views/reports/ModifyReportView/ModifyReportView';

import { useRouteIds } from '../../../hooks/useRouteIds';

interface PublishReportDialogContainerProps {
  isOpen: boolean;
  onClose: () => void;
  report: LiveReportList;
}

export const PublishReportDialogContainer = ({ isOpen, onClose, report }: PublishReportDialogContainerProps) => {
  const intl = useIntl();
  const { workspaceId, organisationId, isConsolidation } = useRouteIds();
  const isPublishing = report.status !== 'live';

  const view = useApiView(
    useMemo(() => new ModifyReportView(workspaceId, organisationId, isConsolidation), [
      workspaceId,
      organisationId,
      isConsolidation
    ])
  );

  const { screen, handleAction: handleConfirm, handleClose } = useDialogWizardState({
    isLoading: view.state.isPublishing,
    error: view.state.errorPublishing,
    onAction: () => view.publishReport({ reportId: report.id, status: isPublishing ? 'live' : 'draft' }),
    onClose
  });

  const title = isPublishing
    ? intl.formatMessage({ id: 'reporting.publish_report', defaultMessage: 'Publish report' })
    : intl.formatMessage({ id: 'reporting.unpublish_report', defaultMessage: 'Unpublish report' });

  const confirmDescription = isPublishing
    ? intl.formatMessage({
      id: 'reporting.publish_report_description',
      defaultMessage: 'Publishing this report will make it available for all users to see'
    })
    : intl.formatMessage({
      id: 'reporting.unpublish_report_description',
      defaultMessage: 'Unpublishing this report will put it back into a draft state'
    });

  const loadingMessage = isPublishing
    ? intl.formatMessage({ id: 'reporting.publishing_report', defaultMessage: 'Publishing report...' })
    : intl.formatMessage({ id: 'reporting.unpublishing_report', defaultMessage: 'Unpublishing report...' });

  const successMessage = isPublishing
    ? intl.formatMessage({ id: 'reporting.report_published', defaultMessage: 'Report published' })
    : intl.formatMessage({ id: 'reporting.report_unpublished', defaultMessage: 'Report unpublished' });

  const errorMessage = view.state.errorPublishing?.message;

  return (
    <ActionDialogWizard
      isOpen={isOpen}
      onClose={handleClose}
      onConfirm={handleConfirm}
      screen={screen}
      title={title}
      subtitle={report.name}
      confirmTitle={intl.formatMessage({ id: 'common.confirm', defaultMessage: 'Confirm' })}
      confirmDescription={confirmDescription}
      loadingMessage={loadingMessage}
      successMessage={successMessage}
      errorMessage={errorMessage}
      dataTestId="publish-report-dialog"
    />
  );
};
